import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { useLang } from '../i18n/LangContext';
import { MoveHorizontal, Columns2, ArrowRight } from 'lucide-react';

// Accessible before/after comparison.
// - Drag anywhere on the image, or use the keyboard-accessible range slider.
// - "View both" toggle shows the two images stacked, with no dragging required.
// - All labels/notice are rendered as webpage text (translatable), not baked into images.
export default function BeforeAfter({
  beforeSrc, afterSrc, beforeAlt, afterAlt,
  beforeLabel, afterLabel, explanation, onExplore, showNotice = true,
}) {
  const { t } = useLang();
  const c = t.compare;
  const rangeId = useId();
  const [pos, setPos] = useState(50);
  const [bothView, setBothView] = useState(false);
  const [containerW, setContainerW] = useState(0);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    if (bothView) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setContainerW(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [bothView]);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e) => { dragging.current = true; updateFromClientX(e.clientX); };
  const onPointerMove = (e) => { if (dragging.current) updateFromClientX(e.clientX); };
  const stopDrag = () => { dragging.current = false; };

  return (
    <div data-testid="before-after" className="w-full">
      <div className="flex items-center justify-end mb-3">
        <button
          type="button"
          onClick={() => setBothView(v => !v)}
          data-testid="before-after-toggle"
          className="inline-flex items-center gap-2 text-xs font-semibold text-[#D4AF37] border border-[#D4AF37]/30 hover:border-[#D4AF37] rounded-full px-3 py-1.5 transition-colors"
        >
          {bothView ? <MoveHorizontal className="w-3.5 h-3.5" /> : <Columns2 className="w-3.5 h-3.5" />}
          {bothView ? c.viewSlider : c.viewBoth}
        </button>
      </div>

      {bothView ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="before-after-both">
          <figure className="m-0">
            <div className="relative rounded-lg overflow-hidden border border-white/10">
              <span className="absolute top-2 left-2 z-10 text-[10px] font-semibold uppercase tracking-wider bg-black/70 text-white px-2 py-1 rounded">{beforeLabel}</span>
              <img src={beforeSrc} alt={beforeAlt} width="1264" height="848" loading="lazy" className="w-full aspect-[3/2] object-cover" />
            </div>
          </figure>
          <figure className="m-0">
            <div className="relative rounded-lg overflow-hidden border border-[#D4AF37]/30">
              <span className="absolute top-2 left-2 z-10 text-[10px] font-semibold uppercase tracking-wider bg-[#D4AF37] text-black px-2 py-1 rounded">{afterLabel}</span>
              <img src={afterSrc} alt={afterAlt} width="1264" height="848" loading="lazy" className="w-full aspect-[3/2] object-cover" />
            </div>
          </figure>
        </div>
      ) : (
        <div
          ref={containerRef}
          className="relative w-full aspect-[3/2] rounded-lg overflow-hidden border border-white/10 select-none touch-none cursor-ew-resize"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={stopDrag}
          onPointerLeave={stopDrag}
          data-testid="before-after-slider"
        >
          {/* After (base layer) */}
          <img src={afterSrc} alt={afterAlt} width="1264" height="848" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          <span className="absolute top-2 right-2 z-10 text-[10px] font-semibold uppercase tracking-wider bg-[#D4AF37] text-black px-2 py-1 rounded pointer-events-none">{afterLabel}</span>

          {/* Before (clipped overlay) */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
            <img src={beforeSrc} alt={beforeAlt} width="1264" height="848" className="absolute inset-0 h-full object-cover" style={{ width: containerW ? `${containerW}px` : '100%', maxWidth: 'none' }} />
            <span className="absolute top-2 left-2 z-10 text-[10px] font-semibold uppercase tracking-wider bg-black/70 text-white px-2 py-1 rounded">{beforeLabel}</span>
          </div>

          {/* Divider handle */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none" style={{ left: `${pos}%` }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center">
              <MoveHorizontal className="w-4 h-4 text-[#0B0D0F]" />
            </div>
          </div>
        </div>
      )}

      {/* Accessible control — works without dragging, keyboard operable */}
      {!bothView && (
        <div className="mt-3">
          <label htmlFor={rangeId} className="sr-only">{c.dragHint}</label>
          <input
            id={rangeId}
            type="range"
            min="0"
            max="100"
            value={Math.round(pos)}
            onChange={(e) => setPos(Number(e.target.value))}
            data-testid="before-after-range"
            aria-label={c.dragHint}
            className="w-full accent-[#D4AF37] cursor-pointer"
          />
          <p className="text-slate-500 text-xs text-center mt-1">{c.dragHint}</p>
        </div>
      )}

      {explanation && <p className="text-slate-300 text-sm leading-relaxed mt-4">{explanation}</p>}

      {showNotice && <p className="text-slate-500 text-xs italic mt-3 border-l-2 border-[#D4AF37]/40 pl-3" data-testid="ai-visualization-notice">{c.notice}</p>}

      {onExplore && (
        <button
          type="button"
          onClick={onExplore}
          data-testid="explore-similar-btn"
          className="group mt-5 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-black font-bold px-6 py-3 rounded-lg transition-all"
        >
          {c.exploreSimilar}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      )}
    </div>
  );
}
